import React, { useState } from "react";
import axios from "axios";
import { Send, CheckCircle2, Loader2, Building2, ShieldCheck } from "lucide-react";
import { ownershipChoices, serviceIntensityChoices, company } from "../mock";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const numberFields = [
  { key: "revenue", label: "Annual Revenue (R)", placeholder: "e.g. 45 000 000", hint: "Total annual turnover" },
  { key: "employees", label: "Employee Headcount", placeholder: "e.g. 55", hint: "Total number of staff" },
  { key: "procurement", label: "Annual Procurement Spend (R)", placeholder: "e.g. 20 000 000", hint: "Total measured procurement" },
  { key: "skills", label: "Annual Skills Development Spend (R)", placeholder: "e.g. 800 000", hint: "Training & development budget" },
  { key: "branches", label: "Number of Branches / Sites", placeholder: "e.g. 2", hint: "Operating locations" },
];

const inputCls =
  "w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-[15px] outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition";

export default function QuoteCalculator() {
  const { toast } = useToast();
  const [inputs, setInputs] = useState({
    revenue: "",
    employees: "",
    procurement: "",
    skills: "",
    branches: "1",
    ownership: "simple",
    serviceIntensity: "standard",
  });
  const [contact, setContact] = useState({ name: "", company: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (k, v) => setInputs((p) => ({ ...p, [k]: v }));
  const setC = (k, v) => setContact((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email) {
      toast({ title: "Missing details", description: "Please provide your name and email address.", variant: "destructive" });
      return;
    }
    if (!inputs.revenue || !inputs.employees) {
      toast({ title: "A few more details", description: "Please add at least your annual revenue and employee headcount.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, { contact, inputs });
      setSent(true);
      toast({ title: "Quote request sent!", description: "Thank you — our team will prepare your tailored pricing and be in touch shortly." });
    } catch (err) {
      toast({
        title: "Could not send",
        description: "Something went wrong. Please try again or WhatsApp us directly.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-slate-100 shadow-[0_20px_50px_-30px_rgba(11,18,32,.3)] p-10 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-50 grid place-items-center mx-auto mb-5">
          <CheckCircle2 size={40} className="text-emerald-500" />
        </div>
        <h3 className="font-head font-bold text-2xl text-[#0B1F3A]">Your request is on its way</h3>
        <p className="text-slate-500 mt-3 leading-relaxed">
          Thank you, {contact.name.split(" ")[0]}. We've received your business details and our specialists
          are preparing a <span className="font-semibold text-[#184E9E]">tailored quote just for you</span>.
          We'll email or call you shortly with your personalised pricing.
        </p>
        <a
          href={`https://wa.me/${company.whatsapp}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-[#184E9E] hover:underline"
        >
          Need it urgently? Chat with us on WhatsApp →
        </a>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl border border-slate-100 shadow-[0_20px_50px_-30px_rgba(11,18,32,.3)] p-6 md:p-9"
      >
        {/* Business details */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#0B1F3A] grid place-items-center text-[#D4AF37]">
            <Building2 size={22} />
          </div>
          <div>
            <h3 className="font-head font-bold text-[#0B1F3A] text-lg">Tell us about your business</h3>
            <p className="text-sm text-slate-500">A few details help us prepare accurate, tailored pricing for you.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {numberFields.map((f) => (
            <div key={f.key}>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{f.label}</label>
              <input
                type="number"
                min="0"
                value={inputs[f.key]}
                onChange={(e) => setField(f.key, e.target.value)}
                placeholder={f.placeholder}
                className={inputCls}
              />
              <span className="text-[12px] text-slate-400">{f.hint}</span>
            </div>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-5 mt-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Ownership / Group Complexity</label>
            <select value={inputs.ownership} onChange={(e) => setField("ownership", e.target.value)} className={`${inputCls} bg-white`}>
              {ownershipChoices.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Service Intensity</label>
            <select value={inputs.serviceIntensity} onChange={(e) => setField("serviceIntensity", e.target.value)} className={`${inputCls} bg-white`}>
              {serviceIntensityChoices.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Contact details */}
        <div className="mt-8 pt-7 border-t border-slate-100">
          <h4 className="font-head font-bold text-[#0B1F3A] text-lg mb-1">Where should we send your quote?</h4>
          <p className="text-sm text-slate-500 mb-5">Your details go straight to our consultants — no obligation.</p>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name *</label>
              <input value={contact.name} onChange={(e) => setC("name", e.target.value)} placeholder="Your name" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Company Name</label>
              <input value={contact.company} onChange={(e) => setC("company", e.target.value)} placeholder="Company (optional)" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address *</label>
              <input type="email" value={contact.email} onChange={(e) => setC("email", e.target.value)} placeholder="you@company.com" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone Number</label>
              <input value={contact.phone} onChange={(e) => setC("phone", e.target.value)} placeholder="+27 ..." className={inputCls} />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="btn-gold w-full mt-7 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-lg disabled:opacity-70"
        >
          {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          {submitting ? "Sending your request…" : "Request My Tailored Quote"}
        </button>

        <p className="flex items-center justify-center gap-2 text-[13px] text-slate-400 mt-4">
          <ShieldCheck size={15} className="text-[#D4AF37]" />
          Your information is kept private and used only to prepare your quote.
        </p>
      </form>
    </div>
  );
}
